import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    useTheme
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { useForm, Controller } from "react-hook-form";
import { ComponentProps, FC, useEffect, useMemo, useState } from "react";
import { PaymentClient, PaymentMethod } from "../../../../utilities/backend/payment";
import { useSelector } from "react-redux";
import { RootState } from "../../../../utilities/redux/store";
import {
    useStripe,
    useElements,
    CardNumberElement,
    CardCvcElement,
    CardExpiryElement
} from "@stripe/react-stripe-js";

const cardElementOptions: ComponentProps<typeof CardNumberElement>['options'] = {
    style: {
        base: {
            fontSize: "16px",
            color: "#000",
            "::placeholder": {
                color: "#aaa",
            },
        },
        invalid: {
            color: "#ff1744",
        },
    },
};

interface DonateProps {
    investmentAmount: number,
    minimumInvestmentAmount: number,
    onSuccess: () => void,
    anonymousStates: Record<"email" | "firstName" | "lastName" | "isAnonymous", string> | null,
}

const Donate: FC<DonateProps> = ({ investmentAmount, minimumInvestmentAmount, onSuccess, anonymousStates }) => {

    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<{ success: boolean, message: string } | null>(null);

    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod['id']>("new");
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const theme = useTheme();

    const stripe = useStripe();
    const elements = useElements();

    const iswithOutAuth = useSelector((state: RootState) => state.token.token) === null;

    const {
        handleSubmit,
        control,
        register,
        reset,
        formState: { errors, isSubmitting },
        resetField,
        setValue
    } = useForm({
        defaultValues: {
            amount: investmentAmount,
            cardNumber: "",
            expirationDate: "",
            securityCode: "",
            country: "US",
            zipCode: "",
            phoneNumber: "",
            rememberCardDetail: false,
        },
    });

    const changePaymentMethod: ComponentProps<typeof RadioGroup>['onChange'] = (_, value) => {
        setSelectedMethod(value);
        resetField("securityCode");
    }

    const onSubmit = async (formData: any) => {
        let paymentData;

        if (selectedMethod === "new") {

            if (!stripe || !elements) {
                return;
            }

            const cardElement = elements.getElement(CardNumberElement);

            if (!cardElement) {
                console.error("CardNumberElement not found.");
                setLoading(false);
                return;
            }

            const { token, error } = await stripe.createToken(cardElement);

            if (error) {
                setApiResponse({ success: false, message: error.message || "An unexpected error occurred." });
                setLoading(false);
                return;
            }

            paymentData = {
                amount: Number(formData.amount),
                rememberCardDetail: formData.rememberCardDetail,
                phoneNumber: formData.phoneNumber,
                billingDetails: {
                    country: formData.country,
                    zipCode: formData.zipCode
                },
                tokenId: token.id,
            };
        } else {
            paymentData = {
                amount: Number(formData.amount),
                paymentMethodId: selectedMethod,
                cvc: formData.securityCode,
                phoneNumber: formData.phoneNumber,
                billingDetails: {
                    country: formData.country,
                    zipCode: formData.zipCode
                }
            };
        }

        if (!anonymousStates && iswithOutAuth) {
            paymentData = {
                ...paymentData,
                userName: localStorage.getItem("userName") ?? null
            };
        }

        if (anonymousStates) {
            paymentData = {
                ...paymentData,
                ...anonymousStates
            }
        }

        try {
            const response = await paymentClient.make_payment(paymentData);

            if (response?.success) {
                setApiResponse({ success: true, message: response?.message });
                reset();
                onSuccess();
            } else {
                throw new Error(response?.message || "Payment failed.");
            }
        } catch (err: any) {
            console.error("Payment Error:", err);
            setApiResponse({ success: false, message: err?.message || "An unexpected error occurred." });
        }
    };

    const handleInputChange = (field: "phoneNumber" | "expirationDate" | "cardNumber", value: string) => {
        let formattedValue = value.replace(/\D/g, ""); // Remove non-numeric characters

        if (field === "phoneNumber") {
            formattedValue = formattedValue.slice(0, 10); // Limit to 10 digits
            if (formattedValue.length >= 6) {
                formattedValue = `(${formattedValue.slice(0, 3)}) ${formattedValue.slice(3, 6)}-${formattedValue.slice(6)}`;
            } else if (formattedValue.length >= 3) {
                formattedValue = `(${formattedValue.slice(0, 3)}) ${formattedValue.slice(3)}`;
            }
        } else if (field === "expirationDate") {
            formattedValue = formattedValue.slice(0, 4); // Limit to MMYY
            if (formattedValue.length >= 3) {
                formattedValue = `${formattedValue.slice(0, 2)}/${formattedValue.slice(2)}`; // Format MM/YY
            }
        } else if (field === "cardNumber") {
            formattedValue = formattedValue.slice(0, 16); // Limit to 16 digits
            formattedValue = formattedValue.replace(/(.{4})/g, "$1 ").trim(); // Add spaces every 4 digits
        }

        setValue(field, formattedValue, { shouldValidate: true, shouldTouch: true }); // Update form value
    };

    const paymentClient = new PaymentClient();

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                setLoading(true);
                const response = await paymentClient.getPaymentMethodDetails();
                if (response.success) {
                    setPaymentMethods(response.data);
                    if (response.data.length > 0) {
                        setSelectedMethod(response.data[0].id);
                    }
                } else {
                    throw new Error(response?.message);
                }
            } catch (err: any) {
                setPaymentMethods([]);
                console.error("Error fetching payment methods:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPaymentMethods();
    }, []);

    const minimumAmount = useMemo(() => {
        const recommendationData = localStorage.getItem("recommendationData");
        if (recommendationData) {
            const parsedData = JSON.parse(recommendationData);
            return parsedData.campaign.minimumInvestment
        }
        return minimumInvestmentAmount;
    }, [minimumInvestmentAmount]);

    const minimumAmountMessage = useMemo(() => {
        const recommendationData = localStorage.getItem("recommendationData");
        if (recommendationData) {
            const parsedData = JSON.parse(recommendationData);
            return `Thank you for your interest in ${parsedData.campaign.name}.The minimum investment is $${minimumAmount}.`;
        }
        return `Minimum amount is $${minimumAmount}`;
    }, [minimumAmount]);

    return (
        <Stack alignItems={"center"} justifyContent={"center"} width={"100%"}>
            <Box sx={{ width: "100%", p: 3, bgcolor: "#f8f9fa", borderRadius: 2, marginTop: "30px" }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth margin="normal">
                        <Typography variant="body2">Amount</Typography>
                        <TextField
                            variant="outlined"
                            placeholder="Enter amount"
                            type="number"
                            inputProps={{ step: "any" }}
                            {...register("amount",
                                {
                                    required: "Amount is required",
                                    min: { value: minimumAmount, message: minimumAmountMessage },
                                }
                            )}
                            error={!!errors.amount}
                            sx={{ bgcolor: "white" }}
                        />
                        {errors.amount && (
                            <Typography color="error" variant="caption">
                                {errors.amount.message}
                            </Typography>
                        )}
                    </FormControl>

                    {paymentMethods.length > 0 &&
                        <RadioGroup value={selectedMethod} onChange={changePaymentMethod}>
                            {
                                loading && (
                                    <Box display={"flex"} justifyContent="center" alignItems="center" pb={1} mb={1}>
                                        <CircularProgress size={23} color="inherit" />
                                    </Box>
                                )
                            }
                            {
                                paymentMethods.map(method => (
                                    <Box key={method.id} display="flex" justifyContent="start" alignItems="center" borderBottom="1px solid #ddd" pb={1} mb={1}>
                                        <FormControlLabel
                                            value={method.id}
                                            control={<Radio />}
                                            label={<Typography variant="body1">{method.brand.toUpperCase()} ****{method.last4}</Typography>}
                                        />
                                    </Box>
                                ))
                            }

                            <FormControlLabel value="new" control={<Radio />} label="Use a new card" />
                        </RadioGroup>
                    }
                    {
                        selectedMethod === "new" && (
                            <>
                                <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                                    {/* Card Number Field with Input Mask */}
                                    <FormControl sx={{ flex: 0.5 }} margin="normal">
                                        <Typography variant="body2">Card Number</Typography>
                                        <Controller
                                            name="cardNumber"
                                            control={control}
                                            rules={{ required: "Card number is required" }}
                                            render={({ field }) => (
                                                <div
                                                    style={{
                                                        border: `1px solid ${errors.cardNumber ? "#ff1744" : "#ccc"}`,
                                                        padding: "10px",
                                                        borderRadius: "4px",
                                                        backgroundColor: "white",
                                                    }}
                                                >
                                                    <CardNumberElement
                                                        options={cardElementOptions}
                                                        onChange={(e) => field.onChange(e.complete ? e : null)}
                                                    />
                                                </div>
                                            )}
                                        />
                                        {errors.cardNumber && (
                                            <Typography color="error" variant="caption">
                                                {errors.cardNumber.message}
                                            </Typography>
                                        )}
                                    </FormControl>
                                    <FormControl sx={{ flex: 0.25 }} margin="normal">
                                        <Typography variant="body2">Expiration Date</Typography>
                                        <Controller
                                            name="expirationDate"
                                            control={control}
                                            rules={{ required: "Expiration date is required" }}
                                            render={({ field }) => (
                                                <div
                                                    style={{
                                                        border: `1px solid ${errors.expirationDate ? "#ff1744" : "#ccc"}`,
                                                        padding: "10px",
                                                        borderRadius: "4px",
                                                        backgroundColor: "white",
                                                    }}
                                                >
                                                    <CardExpiryElement
                                                        options={cardElementOptions}
                                                        onChange={(e) => field.onChange(e.complete ? e : null)}
                                                    />
                                                </div>
                                            )}
                                        />
                                        {errors.expirationDate && (
                                            <Typography color="error" variant="caption">
                                                {errors.expirationDate.message}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    <FormControl sx={{ flex: 0.25 }} margin="normal">
                                        <Typography variant="body2">Security Code</Typography>
                                        <Controller
                                            name="securityCode"
                                            control={control}
                                            rules={{ required: "Security code is required" }}
                                            render={({ field }) => (
                                                <div
                                                    style={{
                                                        border: `1px solid ${errors.securityCode ? "#ff1744" : "#ccc"}`,
                                                        padding: "10px",
                                                        borderRadius: "4px",
                                                        backgroundColor: "white",
                                                    }}
                                                >
                                                    <CardCvcElement
                                                        options={cardElementOptions}
                                                        onChange={(e) => field.onChange(e.complete ? e : null)}
                                                    />
                                                </div>
                                            )}
                                        />
                                        {errors.securityCode && (
                                            <Typography color="error" variant="caption">
                                                {errors.securityCode.message}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Stack>
                            </>
                        )
                    }
                    <Stack direction="row" spacing={2} sx={{ my: 2 }}>

                        <FormControl fullWidth margin="normal" sx={{ flex: 0.5 }}>
                            <Typography variant="body2">Country</Typography>
                            <Controller
                                name="country"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} sx={{ bgcolor: "white" }}>
                                        <MenuItem value="US">United States</MenuItem>
                                        <MenuItem value="CA">Canada</MenuItem>
                                        <MenuItem value="UK">United Kingdom</MenuItem>
                                    </Select>
                                )}
                            />
                        </FormControl>

                        <FormControl fullWidth margin="normal" sx={{ flex: 0.5 }}>
                            <Typography variant="body2">ZIP Code</Typography>
                            <TextField
                                variant="outlined"
                                placeholder="12345"
                                {...register("zipCode", {
                                    required: "ZIP Code is required",
                                    pattern: {
                                        value: /^\d{5}$/,
                                        message: "Invalid ZIP Code",
                                    },
                                })}
                                error={!!errors.zipCode}
                                sx={{ bgcolor: "white" }}
                            />
                            {errors.zipCode && (
                                <Typography color="error" variant="caption">
                                    {errors.zipCode.message}
                                </Typography>
                            )}
                        </FormControl>
                    </Stack>

                    <FormControl fullWidth margin="normal">
                        <Typography variant="body2">Phone Number</Typography>
                        <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{ required: "Phone number is required" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    variant="outlined"
                                    placeholder="(555) 555-1234"
                                    error={!!errors.phoneNumber}
                                    onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                                    inputProps={{ maxLength: 14 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon />
                                            </InputAdornment>
                                        ),
                                        sx: { bgcolor: "white" },
                                    }}
                                />
                            )}
                        />
                        {errors.phoneNumber && (
                            <Typography color="error" variant="caption">
                                {errors.phoneNumber.message}
                            </Typography>
                        )}
                    </FormControl>
                    {
                        selectedMethod == "new" && (
                            <FormControlLabel
                                control={
                                    <Controller
                                        name="rememberCardDetail"
                                        control={control}
                                        render={({ field }) => <Checkbox {...field} checked={field.value} sx={{ color: "black" }} />}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        If desired, save your card details on Stripe. The Autofill link feature is also part of Stripe. CataCap is not saving any credit card details.
                                    </Typography>
                                }
                            />
                        )
                    }
                    <Button
                        disabled={isSubmitting}
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            mt: 2,
                            bgcolor: "#80B73F",
                            color: "#FFF",
                            padding: "10px 20px",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#9DCF5F" },
                            [theme.breakpoints.down("sm")]: {
                                width: "346px",
                            },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={23} color="inherit" /> : "Submit"}
                    </Button>
                </form>
            </Box>
            {
                apiResponse && (
                    <Snackbar
                        open={true}
                        autoHideDuration={6000}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                        onClose={() => setApiResponse(null)}
                    >
                        <Alert
                            severity={apiResponse.success ? 'success' : 'error'}
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {apiResponse.message}
                        </Alert>
                    </Snackbar>
                )
            }
        </Stack>
    );
};

export default Donate;
